import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";
import type { Link, LinkProps } from "react-router-dom";
import type { SpaceProps } from "styled-system";

// Sizes
export enum buttonSize {
  XL = "xl",
  L = "l",
  M = "m",
  S = "s",
  XS = "xs",
}

export const buttonSizes = Object.freeze({ ...buttonSize });
export type ButtonSizes = (typeof buttonSizes)[keyof typeof buttonSizes];

// Variants
export enum buttonVariant {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  TERTIARY = "tertiary",
  TEXT_COLORED = "text_colored",
  TEXT_DARK = "text_dark",
}

export const buttonVariants = Object.freeze({ ...buttonVariant });
export type ButtonVariants =
  (typeof buttonVariants)[keyof typeof buttonVariants];

type ButtonTypes =
  | ButtonHTMLAttributes<HTMLButtonElement>
  | AnchorHTMLAttributes<HTMLAnchorElement>
  | LinkProps;

export type ButtonProps = {
  ref?: React.RefObject<HTMLButtonElement>;
  text?: string;
  variant?: ButtonVariants;
  size?: ButtonSizes;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  as?: "a" | "button" | typeof Link;
  href?: string;
  external?: boolean;
  $isLoading?: boolean;
  disabled?: boolean;
  loading?: boolean;
  $error?: boolean;
  $onlyIcon?: boolean;
  initChatSession?: boolean;
} & ButtonTypes &
  SpaceProps;

export type ButtonThemeVariant = {
  background: (error: boolean | undefined) => string;
  backgroundHover: (error: boolean | undefined) => string;
  backgroundActive: (error: boolean | undefined) => string;
  backgroundDisabled: (error: boolean | undefined) => string;
  border: (error: boolean | undefined) => string;
  borderActive: (error: boolean | undefined) => string;
  borderShadowActive: (error: boolean | undefined) => string;
  borderHover: (error: boolean | undefined) => string;
  borderDisabled: (error: boolean | undefined) => string;
  color: (error: boolean | undefined) => string;
  colorHover?: (error: boolean | undefined) => string;
  disabledColor: (error: boolean | undefined) => string;
};

export type ButtonTheme = {
  [key in ButtonVariants]: ButtonThemeVariant;
};

export type ButtonSizeVariant = {
  width: string;
  height: string;
  fontSize?: string;
};

export type ButtonSize = {
  [key in ButtonSizes]: ButtonSizeVariant;
};
